import { mapPathControls } from './pathEditing';
import type { Annotation, DrawingDocument, Point2D } from '../model';
import type { EditorSelection } from './editor.ts';
import { moveAtoms } from './atomGeometryMutations.ts';
import { renderableAnnotations } from './annotationRendering.ts';
import { documentToAtoms, documentToLines } from './drawingDocumentProjections.ts';
import { ATOM_LABEL_MARGIN, boundsOfPoints as boundsOrNull } from './pageExtent.ts';
import { annotationExtentPoints } from '../rendering/annotationExtents.ts';
import { ionicPairs } from './ionicPairs.ts';
import type { ReactionZone } from './reactionGraph.ts';

/**
 * #568 (corrected scope): Align/Distribute operate on *objects* -- a connected molecule
 * fragment, a free-floating reaction arrow, or a decorative shape -- moved as rigid units, not on
 * individual atom coordinates. Selecting part of a molecule still moves the whole molecule: you
 * can't align "half a structure" without breaking its bonds, so touching any atom of a fragment
 * pulls in every atom of that fragment.
 *
 * Scope for what counts as an alignable object (v1):
 * - A connected component of the atom/bond graph touched by the selection (its own atoms +
 *   bonds move together as one rigid unit, preserving internal geometry).
 * - A `reaction-arrow` annotation in the selection whose `from`/`to` are BOTH free points (kind
 *   'point') -- an arrow anchored to a specific atom/bond/lone-pair is a *derived* position, not
 *   an independent object: it already follows its anchor when that atom's fragment moves, and
 *   translating it independently would be wrong.
 * - A `shape` annotation in the selection (ellipse/rect/triangle/star/path).
 * - A `text` annotation in the selection (#855).
 * - #1151 (owner) -- a salt's ions are one object: the pieces the paper joins with a bolt.
 * - #1151 (owner) -- with the reaction's parts given ({@link ReactionParts}) and its arrow selected:
 *   the whole reaction selected on its own lines up as its reactants, the arrow with the Reagents &
 *   catalysts and Solvents boxes as one unit, and its products; selected with anything else it is
 *   one object. The arrow selected with only some of the reaction: the arrow and each box's
 *   selected contents act on their own, a box as a block whose lines keep their rows.
 * `curved-arrow` (mechanism arrows, always atom/bond-anchored by design) and `note`/`tag`/
 * `bond-angle` annotations (anchored to other things, not freely positioned) are not alignable
 * objects.
 *
 * #855 -- bounds are what each object draws (#771 rendered bounds): an annotation's extent
 * (arrowheads, a rotated shape, a text block), a molecule's atoms widened by the label margin the
 * page extent uses.
 */
export type AlignKind =
  | 'alignLeft'
  | 'alignCenterH'
  | 'alignRight'
  | 'alignTop'
  | 'alignMiddle'
  | 'alignBottom'
  | 'distributeHorizontal'
  | 'distributeVertical';

export interface Bounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

type AlignableObject =
  | { readonly kind: 'molecule'; readonly atomIds: readonly string[]; readonly bounds: Bounds }
  | { readonly kind: 'arrow'; readonly annotationId: string; readonly bounds: Bounds }
  | { readonly kind: 'shape'; readonly annotationId: string; readonly bounds: Bounds }
  | { readonly kind: 'text'; readonly annotationId: string; readonly bounds: Bounds }
  // #1151 -- objects moved as one: a reaction, its arrow unit, or a box's lines (`rows`: aligned
  // left, centre or right each line lines up on its own, keeping its row).
  | {
      readonly kind: 'group';
      readonly members: readonly AlignableObject[];
      readonly bounds: Bounds;
      readonly rows?: boolean;
    };

/** #1151 -- the reaction as the paper sees it: its arrow, and what each box holds (a salt's ions
 *  together), the texts by the arrow included. */
export interface ReactionParts {
  readonly arrowId: string;
  readonly molecules: readonly { readonly zone: ReactionZone; readonly atomIds: readonly string[] }[];
  readonly texts: readonly { readonly zone: 'above' | 'below'; readonly annotationId: string }[];
}

/** #855 -- Align against the other selected objects (default), or move the selection as one
 *  block against the page sheet (#771 R7). `spacing` (drawing units) makes Distribute use that
 *  exact gap instead of equal gaps over the current span (A6). */
export interface AlignOptions {
  readonly relativeTo?: 'objects' | 'page';
  readonly page?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly spacing?: number;
  /** #1151 -- the reaction's parts, so the reaction and its boxes are arranged as such. */
  readonly reaction?: ReactionParts | null;
}

// Every annotation's drawn extent in depiction coordinates, keyed by id.
export function annotationBounds(document: DrawingDocument): Map<string, Bounds> {
  const coordinateSet = document.depiction.coordinateSet;
  const size = { width: Math.max(1, coordinateSet.width), height: Math.max(1, coordinateSet.height) };
  const renderable = renderableAnnotations(
    document.annotations ?? [],
    documentToAtoms(document),
    documentToLines(document),
    size,
    size,
    false
  );
  const result = new Map<string, Bounds>();
  for (const annotation of renderable) {
    const bounds = boundsOrNull(
      annotationExtentPoints(annotation, size).map((point) => ({ x: point.x * size.width, y: point.y * size.height }))
    );
    if (bounds) result.set(annotation.id, bounds);
  }
  return result;
}

function connectedComponent(document: DrawingDocument, startAtomId: string): Set<string> {
  const adjacency = new Map<string, string[]>();
  for (const edge of document.structure.graph.edges) {
    adjacency.set(edge.source, [...(adjacency.get(edge.source) ?? []), edge.target]);
    adjacency.set(edge.target, [...(adjacency.get(edge.target) ?? []), edge.source]);
  }
  const seen = new Set<string>([startAtomId]);
  const queue = [startAtomId];
  for (let index = 0; index < queue.length; index += 1) {
    for (const neighbor of adjacency.get(queue[index]!) ?? []) {
      if (!seen.has(neighbor)) {
        seen.add(neighbor);
        queue.push(neighbor);
      }
    }
  }
  return seen;
}

function boundsOfPoints(points: readonly Point2D[]): Bounds {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

function isFreeFloatingReactionArrow(annotation: Annotation): annotation is Annotation & {
  kind: 'reaction-arrow';
  from: { kind: 'point'; at: Point2D };
  to: { kind: 'point'; at: Point2D };
} {
  return annotation.kind === 'reaction-arrow' && annotation.from.kind === 'point' && annotation.to.kind === 'point';
}

/** #1151 -- the atoms of `atomId`'s piece and of every ion the paper bolts to it, transitively. */
function saltOrComponent(
  document: DrawingDocument,
  atomId: string,
  pairs: readonly { readonly cationId: string; readonly anionId: string }[]
): Set<string> {
  const atoms = connectedComponent(document, atomId);
  for (let grown = pairs.length > 0; grown;) {
    grown = false;
    for (const { cationId, anionId } of pairs) {
      const partner =
        atoms.has(cationId) && !atoms.has(anionId)
          ? anionId
          : atoms.has(anionId) && !atoms.has(cationId)
            ? cationId
            : null;
      if (partner === null) continue;
      for (const id of connectedComponent(document, partner)) atoms.add(id);
      grown = true;
    }
  }
  return atoms;
}

function unitedBounds(objects: readonly AlignableObject[]): Bounds {
  return {
    minX: Math.min(...objects.map(({ bounds }) => bounds.minX)),
    maxX: Math.max(...objects.map(({ bounds }) => bounds.maxX)),
    minY: Math.min(...objects.map(({ bounds }) => bounds.minY)),
    maxY: Math.max(...objects.map(({ bounds }) => bounds.maxY))
  };
}

function group(members: readonly AlignableObject[], rows = false): AlignableObject {
  return members.length === 1 && !rows
    ? members[0]!
    : { kind: 'group', members, bounds: unitedBounds(members), ...(rows ? { rows } : {}) };
}

/**
 * #1151 (owner) -- `objects` arranged as the reaction's parts when its arrow is among them (see
 * the module comment); unchanged otherwise.
 */
function asReaction(objects: AlignableObject[], reaction: ReactionParts): AlignableObject[] {
  const arrow = objects.find((object) => object.kind === 'arrow' && object.annotationId === reaction.arrowId);
  if (!arrow) return objects;
  const zoneOfAtom = new Map<string, ReactionZone>();
  for (const { zone, atomIds } of reaction.molecules) for (const id of atomIds) zoneOfAtom.set(id, zone);
  const zoneOfText = new Map(reaction.texts.map(({ zone, annotationId }) => [annotationId, zone]));
  const zoneOf = (object: AlignableObject): ReactionZone | null =>
    object.kind === 'molecule'
      ? (object.atomIds.map((id) => zoneOfAtom.get(id)).find((zone) => zone !== undefined) ?? null)
      : object.kind === 'text'
        ? (zoneOfText.get(object.annotationId) ?? null)
        : null;
  const inZone = (zone: ReactionZone) => objects.filter((object) => zoneOf(object) === zone);
  const selectedAtoms = new Set(objects.flatMap((object) => (object.kind === 'molecule' ? object.atomIds : [])));
  const selectedTexts = new Set(objects.flatMap((object) => (object.kind === 'text' ? [object.annotationId] : [])));
  const whole =
    reaction.molecules.every(({ atomIds }) => atomIds.every((id) => selectedAtoms.has(id))) &&
    reaction.texts.every(({ annotationId }) => selectedTexts.has(annotationId));
  const others = objects.filter((object) => object !== arrow && zoneOf(object) === null);
  if (whole) {
    const parts = [
      ...inZone('reactants'),
      group([arrow, ...inZone('above'), ...inZone('below')]),
      ...inZone('products')
    ];
    return others.length === 0 ? parts : [group(parts), ...others];
  }
  const boxes = (['above', 'below'] as const).map(inZone).filter((members) => members.length > 0);
  return [
    ...objects.filter((object) => zoneOf(object) !== 'above' && zoneOf(object) !== 'below'),
    ...boxes.map((members) => group(members, true))
  ];
}

/** Every distinct alignable object touched by `selection` -- a molecule fragment is included
 *  once even if only one of its atoms is selected, since the whole fragment moves together.
 *  #1151: a salt's ions are one object, and given the reaction's parts the reaction's are grouped. */
export function alignableObjectsInSelection(
  document: DrawingDocument,
  selection: EditorSelection,
  reaction?: ReactionParts | null
): AlignableObject[] {
  const selectedAtomIds = new Set(selection.atomIds);
  for (const edge of document.structure.graph.edges) {
    if (selection.bondIds.includes(edge.id)) {
      selectedAtomIds.add(edge.source);
      selectedAtomIds.add(edge.target);
    }
  }

  const objects: AlignableObject[] = [];
  const claimedAtomIds = new Set<string>();
  const pairs = selectedAtomIds.size > 0 ? ionicPairs(document) : [];
  for (const atomId of selectedAtomIds) {
    // Defensive: `selection` can transiently reference an atom the document no longer has (e.g.
    // this renders again before useSelectionReconciliation's own cleanup effect runs after a
    // cut/undo) -- skip it rather than building a component around a nonexistent id.
    if (claimedAtomIds.has(atomId) || !document.structure.atoms[atomId]) continue;
    const component = saltOrComponent(document, atomId, pairs);
    for (const id of component) claimedAtomIds.add(id);
    const points = [...component].map((id) => document.depiction.coordinateSet.coordinates[id]!);
    const atomBounds = boundsOfPoints(points);
    objects.push({
      kind: 'molecule',
      atomIds: [...component],
      bounds: {
        minX: atomBounds.minX - ATOM_LABEL_MARGIN,
        maxX: atomBounds.maxX + ATOM_LABEL_MARGIN,
        minY: atomBounds.minY - ATOM_LABEL_MARGIN,
        maxY: atomBounds.maxY + ATOM_LABEL_MARGIN
      }
    });
  }

  const extents = (selection.annotationIds?.length ?? 0) > 0 ? annotationBounds(document) : new Map<string, Bounds>();
  for (const annotationId of selection.annotationIds ?? []) {
    const annotation = document.annotations.find((candidate) => candidate.id === annotationId);
    const bounds = extents.get(annotationId);
    if (!annotation || !bounds) continue;
    if (isFreeFloatingReactionArrow(annotation)) objects.push({ kind: 'arrow', annotationId, bounds });
    else if (annotation.kind === 'shape') objects.push({ kind: 'shape', annotationId, bounds });
    else if (annotation.kind === 'text') objects.push({ kind: 'text', annotationId, bounds });
  }

  return reaction ? asReaction(objects, reaction) : objects;
}

function translateShape(annotation: Extract<Annotation, { kind: 'shape' }>, dx: number, dy: number): Annotation {
  const points = annotation.points;
  return {
    ...annotation,
    ...(annotation.callout && !annotation.callout.attachment
      ? {
          callout: {
            ...annotation.callout,
            tip: { x: annotation.callout.tip.x + dx, y: annotation.callout.tip.y + dy }
          }
        }
      : {}),
    ...(annotation.pathControls
      ? { pathControls: mapPathControls(annotation.pathControls, (p) => ({ x: p.x + dx, y: p.y + dy })) }
      : {}),
    ...(points
      ? { points: points.map((point) => ({ x: point.x + dx, y: point.y + dy })) as [Point2D, Point2D, ...Point2D[]] }
      : {}),
    ...(annotation.bounds
      ? { bounds: { ...annotation.bounds, x: annotation.bounds.x + dx, y: annotation.bounds.y + dy } }
      : {})
  };
}

function translateArrow<T extends Extract<Annotation, { kind: 'reaction-arrow' | 'curved-arrow' }>>(
  annotation: T,
  dx: number,
  dy: number
): T {
  const translatePoint = (anchor: T['from']) =>
    anchor.kind === 'point' ? { kind: 'point' as const, at: { x: anchor.at.x + dx, y: anchor.at.y + dy } } : anchor;
  return { ...annotation, from: translatePoint(annotation.from), to: translatePoint(annotation.to) };
}

/**
 * #637 -- moves the given annotations rigidly by (dx, dy) in depiction coordinates: shapes, text,
 * and every arrow end given as a free point (plus cubic controls). Ends anchored to atoms/bonds
 * follow those atoms instead.
 */
export function translateAnnotations(
  document: DrawingDocument,
  ids: ReadonlySet<string>,
  dx: number,
  dy: number
): DrawingDocument {
  if (ids.size === 0 || (dx === 0 && dy === 0)) return document;
  const annotations = document.annotations.map((annotation) => {
    if (!ids.has(annotation.id)) return annotation;
    if (annotation.kind === 'shape') return translateShape(annotation, dx, dy);
    if (annotation.kind === 'text') {
      return { ...annotation, position: { x: annotation.position.x + dx, y: annotation.position.y + dy } };
    }
    if (annotation.kind === 'reaction-arrow') return translateArrow(annotation, dx, dy);
    if (annotation.kind === 'curved-arrow') {
      const moved = translateArrow(annotation, dx, dy);
      return annotation.controls
        ? {
            ...moved,
            controls: annotation.controls.map((point) => ({ x: point.x + dx, y: point.y + dy })) as [Point2D, Point2D]
          }
        : moved;
    }
    return annotation;
  });
  return { ...document, annotations };
}

/**
 * Aligns or distributes every alignable object the selection touches (see
 * {@link alignableObjectsInSelection}), each moved as a rigid unit in the document's own absolute
 * depiction-coordinate space. Returns `document` unchanged when the command can't apply.
 *
 * - Align against objects (default) needs 2 objects: each moves to the selection's own edge or
 *   centre.
 * - Align to the page (#771 R7) needs 1: the whole selection moves as one block to the page
 *   sheet's edge or centre, keeping the objects' relative positions.
 * - Distribute needs 3 and spaces the *gaps* between drawn bounds (#771 accepted rule), in
 *   geometric order. With equal gaps the two extremes stay put; when the objects already overlap
 *   more than the span allows, nothing moves. With `spacing` (#855 A6) every gap is exactly that,
 *   packed from the leftmost / topmost object, which stays put (the reference-object rules of
 *   #771 R1-R2 replace this anchor when selection order lands).
 */
export function alignObjects(
  document: DrawingDocument,
  selection: EditorSelection,
  kind: AlignKind,
  options: AlignOptions = {}
): DrawingDocument {
  const objects = alignableObjectsInSelection(document, selection, options.reaction);
  const isDistribute = kind === 'distributeHorizontal' || kind === 'distributeVertical';
  const toPage = !isDistribute && options.relativeTo === 'page' && options.page !== undefined;
  if (objects.length < (isDistribute ? 3 : toPage ? 1 : 2)) return document;

  const deltas = objects.map(() => ({ dx: 0, dy: 0 }));

  if (isDistribute) {
    const horizontal = kind === 'distributeHorizontal';
    const start = (bounds: Bounds) => (horizontal ? bounds.minX : bounds.minY);
    const length = (bounds: Bounds) => (horizontal ? bounds.maxX - bounds.minX : bounds.maxY - bounds.minY);
    const order = objects
      .map((object, index) => ({ index, start: start(object.bounds), length: length(object.bounds) }))
      .sort((a, b) => a.start - b.start || a.index - b.index);
    const first = order[0]!;
    const last = order[order.length - 1]!;
    const totalLength = order.reduce((sum, entry) => sum + entry.length, 0);
    const gap =
      options.spacing !== undefined
        ? Math.max(0, options.spacing)
        : (last.start + last.length - first.start - totalLength) / (order.length - 1);
    if (gap < 0) return document;
    let cursor = first.start;
    for (const entry of order) {
      const delta = cursor - entry.start;
      if (horizontal) deltas[entry.index]!.dx = delta;
      else deltas[entry.index]!.dy = delta;
      cursor += entry.length + gap;
    }
  } else {
    const minX = Math.min(...objects.map((object) => object.bounds.minX));
    const maxX = Math.max(...objects.map((object) => object.bounds.maxX));
    const minY = Math.min(...objects.map((object) => object.bounds.minY));
    const maxY = Math.max(...objects.map((object) => object.bounds.maxY));
    if (toPage) {
      const page = options.page!;
      let dx = 0;
      let dy = 0;
      if (kind === 'alignLeft') dx = page.x - minX;
      else if (kind === 'alignRight') dx = page.x + page.width - maxX;
      else if (kind === 'alignCenterH') dx = page.x + page.width / 2 - (minX + maxX) / 2;
      else if (kind === 'alignTop') dy = page.y - minY;
      else if (kind === 'alignBottom') dy = page.y + page.height - maxY;
      else dy = page.y + page.height / 2 - (minY + maxY) / 2; // alignMiddle
      for (const delta of deltas) {
        delta.dx = dx;
        delta.dy = dy;
      }
    } else {
      objects.forEach((object, index) => {
        const { bounds } = object;
        if (kind === 'alignLeft') deltas[index]!.dx = minX - bounds.minX;
        else if (kind === 'alignRight') deltas[index]!.dx = maxX - bounds.maxX;
        else if (kind === 'alignCenterH') deltas[index]!.dx = (minX + maxX) / 2 - (bounds.minX + bounds.maxX) / 2;
        else if (kind === 'alignTop') deltas[index]!.dy = minY - bounds.minY;
        else if (kind === 'alignBottom') deltas[index]!.dy = maxY - bounds.maxY;
        else deltas[index]!.dy = (minY + maxY) / 2 - (bounds.minY + bounds.maxY) / 2; // alignMiddle
      });
    }
  }

  // #1151 -- each group's members move with it; a box's lines aligned left, centre or right each
  // line up on their own (the same edge every other object goes to), keeping their rows.
  const horizontal = kind === 'alignLeft' || kind === 'alignCenterH' || kind === 'alignRight';
  const leaves: {
    readonly object: Exclude<AlignableObject, { kind: 'group' }>;
    readonly dx: number;
    readonly dy: number;
  }[] = [];
  const spread = (object: AlignableObject, dx: number, dy: number) => {
    if (object.kind !== 'group') {
      leaves.push({ object, dx, dy });
      return;
    }
    for (const member of object.members) {
      const own =
        object.rows && horizontal && !toPage
          ? (kind === 'alignLeft' ? object.bounds.minX - member.bounds.minX : 0) +
            (kind === 'alignRight' ? object.bounds.maxX - member.bounds.maxX : 0) +
            (kind === 'alignCenterH'
              ? (object.bounds.minX + object.bounds.maxX) / 2 - (member.bounds.minX + member.bounds.maxX) / 2
              : 0)
          : 0;
      spread(member, dx + own, dy);
    }
  };
  objects.forEach((object, index) => spread(object, deltas[index]!.dx, deltas[index]!.dy));

  const coordinateSet = document.depiction.coordinateSet;
  const movedAtomPositions: Record<string, Point2D> = {};
  const annotationUpdates = new Map<string, Annotation>();
  leaves.forEach(({ object, dx, dy }) => {
    if (dx === 0 && dy === 0) return;
    if (object.kind === 'molecule') {
      for (const atomId of object.atomIds) {
        const coordinate = coordinateSet.coordinates[atomId]!;
        movedAtomPositions[atomId] = { x: coordinate.x + dx, y: coordinate.y + dy };
      }
    } else {
      const moved = translateAnnotations(document, new Set([object.annotationId]), dx, dy).annotations.find(
        (candidate) => candidate.id === object.annotationId
      );
      if (moved) annotationUpdates.set(object.annotationId, moved);
    }
  });

  let next = document;
  if (Object.keys(movedAtomPositions).length > 0) {
    // moveAtoms takes normalized [0,1] points; the deltas above were computed directly in the
    // document's own absolute coordinateSet space, so convert once here at the boundary.
    next = moveAtoms(
      next,
      Object.fromEntries(
        Object.entries(movedAtomPositions).map(([id, point]) => [
          id,
          { x: point.x / coordinateSet.width, y: point.y / coordinateSet.height }
        ])
      )
    );
  }
  if (annotationUpdates.size > 0) {
    next = { ...next, annotations: next.annotations.map((a) => annotationUpdates.get(a.id) ?? a) };
  }
  return next;
}

export type StackingCommand = 'toFront' | 'forward' | 'backward' | 'toBack';

/**
 * #855 -- stacking order for annotations (arrows, shapes, text): later in `document.annotations`
 * draws on top. To Front / To Back move the selected annotations past all the others, keeping
 * their own order; Forward / Backward move each one step past its nearest unselected neighbour.
 * Molecules sit above background shapes (#875) and below foreground annotations; this command
 * preserves order within each layer and does not change a shape's layer.
 * Returns `document` unchanged when nothing would move.
 */
export function reorderAnnotations(
  document: DrawingDocument,
  annotationIds: readonly string[],
  command: StackingCommand
): DrawingDocument {
  const selected = new Set(annotationIds);
  const list = [...(document.annotations ?? [])];
  if (!list.some((annotation) => selected.has(annotation.id))) return document;
  let next: Annotation[];
  if (command === 'toFront' || command === 'toBack') {
    const chosen = list.filter((annotation) => selected.has(annotation.id));
    const others = list.filter((annotation) => !selected.has(annotation.id));
    next = command === 'toFront' ? [...others, ...chosen] : [...chosen, ...others];
  } else {
    next = list;
    if (command === 'forward') {
      for (let index = next.length - 2; index >= 0; index -= 1) {
        if (selected.has(next[index]!.id) && !selected.has(next[index + 1]!.id)) {
          [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
        }
      }
    } else {
      for (let index = 1; index < next.length; index += 1) {
        if (selected.has(next[index]!.id) && !selected.has(next[index - 1]!.id)) {
          [next[index], next[index - 1]] = [next[index - 1]!, next[index]!];
        }
      }
    }
  }
  return next.every((annotation, index) => annotation === document.annotations[index])
    ? document
    : { ...document, annotations: next };
}
